<template>
  <v-btn color="success" @click="exportExcel" :loading="isExporting">
    <v-icon left>mdi-file-excel-box</v-icon> Excel
  </v-btn>
</template>

<script>
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';

export default {
  name: "ExcelExportComponent",
  props: {
    reportData: {
      type: Array,
      required: true
    },
    reportTitle: {
      type: String,
      required: true
    },
    propertyName: {
      type: String,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    visibleColumns: {
      type: Object,
      required: true
    },
    sumField: {
      type: Function,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    },
    formatDate: {
      type: Function,
      required: true
    },
    columnDefinitions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isExporting: false
    };
  },
  methods: {
    async exportExcel() {
      this.isExporting = true;
      
      try {
        // Prepare headers based on visible columns
        const headers = this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .map(col => col.header);

        // Prepare data
        const data = this.reportData.map(item => {
          const row = {};
          this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .forEach(col => {
              row[col.header] = col.formatter ? col.formatter(item[col.field]) : item[col.field];
            });
          return row;
        });

        // Add totals row if needed
        if (this.showTotals) {
          const totalsRow = { 'Reference': 'TOTALS', 'Property': '' };
          this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .forEach(col => {
              if (col.isTotalable) {
                totalsRow[col.header] = this.formatCurrency(this.sumField(col.field));
              }
            });
          data.push(totalsRow);
        }

        // Create worksheet
        const ws = XLSXUtils.json_to_sheet(data, { header: headers });

        // Add styling
        if (!ws['!cols']) ws['!cols'] = [];
        headers.forEach((header, i) => {
          ws['!cols'][i] = { width: 18 };
          const cellRef = XLSXUtils.encode_cell({ c: i, r: 0 });
          ws[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFD9D9D9" } },
            alignment: { horizontal: 'center' }
          };
        });

        // Style totals row if exists
        if (this.showTotals) {
          const totalsRowNum = data.length - 1;
          headers.forEach((_, i) => {
            const cellRef = XLSXUtils.encode_cell({ c: i, r: totalsRowNum });
            ws[cellRef].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "FFE0E0E0" } }
            };
          });
        }

        // Create workbook
        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, "Report Data");

        // Add metadata sheet
        const metadata = [
          [`Report Title: ${this.reportTitle}`],
          [`Property: ${this.propertyName}`],
          [`Report Type: ${this.reportTitle}`],
          [`Date Range: ${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)}`],
          [`Generated: ${new Date().toLocaleDateString()}`],
          ['']
        ];
        const wsMeta = XLSXUtils.aoa_to_sheet(metadata);
        XLSXUtils.book_append_sheet(wb, wsMeta, "Report Info");

        // Save the Excel file
        const fileName = `${this.reportTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSXWriteFile(wb, fileName);

        this.$emit('success', "Report exported to Excel!");
      } catch (error) {
        console.error("Error generating Excel:", error);
        this.$emit('error', "Failed to generate Excel report");
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>